import { useI18n } from "#imports";
import { useNuxtApp } from "#app";
import { getPropValue, isObject } from "@spoferan/spoferan-ts-core";
import { useErrorParser } from "./useErrorParser.mjs";
export function useFormSubmits(options) {
  const { $apiFetch, $auth, $alert, $notify } = useNuxtApp();
  const { t } = useI18n();
  let pendingApiRequestController = null;
  const { parseErrorMessage } = useErrorParser();
  async function submitForm(apiPath, data = {}, requestOptions = { method: "POST", guest: false }) {
    if (pendingApiRequestController) {
      pendingApiRequestController.abort();
    }
    pendingApiRequestController = new AbortController();
    return new Promise((resolve, reject) => {
      $apiFetch(apiPath, {
        method: requestOptions.method ?? "POST",
        body: data,
        guest: requestOptions.guest ?? false,
        silent: true,
        // We handle the errors within the component itself
        signal: pendingApiRequestController.signal
      }).then((response) => {
        handleSuccess(response ?? {});
        resolve(response);
      }).catch((error) => {
        if (error.name === "FetchError" && error.message.startsWith("The user aborted a request.")) {
          return;
        }
        if (requestOptions.method !== "GET" || options.withError) {
          handleError(error);
        }
        reject({
          message: parseErrorMessage(error),
          response: error
        });
      }).finally(() => {
        pendingApiRequestController = null;
      });
    });
  }
  function handleSuccess(response) {
    if (response.redirect) {
      window.location.href = response.redirect;
      return;
    }
    const alert = response.alert;
    const notification = response.notification;
    if ($auth && !$auth.loggedIn && response.login && response.login.token) {
      $auth.setUserToken(response.login.token);
    }
    const params = {};
    if (options.langParams) {
      Object.keys(options.langParams).forEach((langKey) => {
        const responseKey = options.langParams[langKey];
        const responseValue = getPropValue(response.data, responseKey);
        params[langKey] = responseValue !== void 0 ? responseValue : responseKey;
      });
    }
    if (options.silent) {
      callSuccessHandler(response);
      return;
    }
    if (alert) {
      $alert({
        title: alert.title,
        text: alert.message,
        type: alert.type ? alert.type : "success",
        confirmText: alert.accept ? alert.accept : "Ok"
      }).then(() => {
        callSuccessHandler(response);
      });
      return;
    }
    if (options.alert) {
      $alert({
        title: t(`alert.${options.alert}.title`),
        text: t(`alert.${options.alert}.message`, params),
        type: "success",
        confirmText: t("alert.default.post.accept")
      }).then(() => {
        callSuccessHandler(response);
      });
      return;
    }
    if (notification) {
      $notify(notification.type ? notification.type : "success", notification.message, notification.title, notification.duration ? notification.duration : 4500);
      callSuccessHandler(response);
      return;
    }
    if (options.notification || options.notification === "") {
      if (options.notification === true || options.notification === "") {
        $notify("success", t("notification.saved.message"));
      } else if (options.notification) {
        const titleKey = `notification.${options.notification}.title`;
        const title = t(titleKey);
        $notify("success", t(`notification.${options.notification}.message`, params), title === titleKey ? null : title);
      }
      callSuccessHandler(response);
      return;
    }
    callSuccessHandler(response);
  }
  function handleError(error) {
    if (!error || !error.statusCode) {
      const defaultError = {
        message: t("error.unknown")
      };
      showServerErrorAlert(500, defaultError, () => {
        callErrorHandler(500, defaultError);
      });
      return;
    }
    const status = error.statusCode;
    if (status === 422 && error.errors) {
      if (Object.keys(error.errors).length) {
        const failedValidation = error.errors[Object.keys(error.errors)[0]][0];
        const message = isObject(failedValidation) ? failedValidation.message : failedValidation;
        $notify("error", message, t("validation.check_inputs"));
      }
      callErrorHandler(status, error);
    } else {
      showServerErrorAlert(status, error, () => {
        callErrorHandler(status, error);
      });
    }
  }
  function callSuccessHandler(response) {
    if (options.successHandler) {
      options.successHandler(response.data, response);
    }
  }
  function callErrorHandler(status, error) {
    if (options.errorHandler) {
      options.errorHandler(status, error);
    }
  }
  function showServerErrorAlert(status, errorResponse, callback) {
    const notificationStatusCodes = [422];
    const showAsNotification = status && notificationStatusCodes.includes(status) || errorResponse && errorResponse.notification;
    const defaultMessage = errorResponse.display_message ?? (errorResponse.message ?? (errorResponse.error ?? t("error.unknown")));
    if (showAsNotification) {
      const notificationData = errorResponse && errorResponse.notification ? errorResponse.notification : {};
      const title = notificationData.title ? notificationData.title : getErrorTitleByStatus(status);
      const message = notificationData.message ? notificationData.message : defaultMessage;
      const type = notificationData.type ? notificationData.type : "error";
      const duration = notificationData.duration ? notificationData.duration : 4500;
      $notify(type, message, title, duration);
      callback();
    } else {
      const alertData = errorResponse && errorResponse.alert ? errorResponse.alert : {};
      const title = alertData.title ? alertData.title : getErrorTitleByStatus(status);
      const message = alertData.message ? alertData.message : defaultMessage;
      const type = alertData.type ? alertData.type : getErrorTypeByStatus(status);
      const confirmText = alertData.accept ? alertData.accept : "Ok";
      $alert({
        title,
        text: message,
        type,
        confirmText
      }).then(() => {
        callback();
      });
    }
  }
  function getErrorTitleByStatus(errorStatus) {
    switch (errorStatus) {
      case 422:
        return t("validation.check_inputs");
    }
    return "Whoops!";
  }
  function getErrorTypeByStatus(errorStatus) {
    return errorStatus >= 400 && errorStatus <= 499 ? "warning" : "error";
  }
  return {
    submitForm,
    handleSuccess,
    handleError
  };
}
